/**
 * CreatePartiesSection — Embedded parties table for the Create Facility modal
 *
 * Sprint: SPRINT-30
 * Feature: F015 — Create Facility Modal Section 2 (Parties)
 *
 * Replaces the single "Borrower" selector with a full parties table.
 * Supports add/remove, role selection, and primary flag.
 * At least one borrower or co_borrower is required for form submission.
 */

import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, X } from "lucide-react";
import type { Account, Contact } from "@shared/schema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreatePartyEntry {
  /** Client-side key for list management */
  key: string;
  partyType: "account" | "contact";
  partyId: string;
  partyName: string;
  partyRole: string;
  isPrimary: boolean;
}

export interface CreatePartiesSectionProps {
  parties: CreatePartyEntry[];
  onChange: (parties: CreatePartyEntry[]) => void;
  clientGroupId?: string;
  /** Validation error message (set by parent form) */
  error?: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PARTY_ROLES = [
  { value: "borrower", label: "Borrower" },
  { value: "co_borrower", label: "Co-Borrower" },
  { value: "guarantor", label: "Guarantor" },
  { value: "security_provider", label: "Security Provider" },
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreatePartiesSection({
  parties,
  onChange,
  clientGroupId,
  error,
}: CreatePartiesSectionProps) {
  const [showAddRow, setShowAddRow] = useState(false);
  const [addPartyType, setAddPartyType] = useState<"account" | "contact">("account");
  const [addPartyId, setAddPartyId] = useState("");
  const [addPartyRole, setAddPartyRole] = useState("borrower");

  // Fetch accounts for party selector
  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["/api/crm/accounts", { ...(clientGroupId ? { clientGroupId } : {}) }],
    enabled: showAddRow && addPartyType === "account",
  });

  // Fetch contacts for party selector
  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", { ...(clientGroupId ? { clientGroupId } : {}) }],
    enabled: showAddRow && addPartyType === "contact",
  });

  const resolvePartyName = useCallback(
    (type: "account" | "contact", id: string): string => {
      if (type === "account") {
        const account = accounts.find((a) => a.id === id);
        return account?.legalName ?? "Unknown Account";
      }
      const contact = contacts.find((c) => c.id === id);
      return contact ? `${contact.firstName ?? ""} ${contact.lastName ?? ""}`.trim() : "Unknown Contact";
    },
    [accounts, contacts],
  );

  const handleAdd = useCallback(() => {
    if (!addPartyId) return;

    const name = resolvePartyName(addPartyType, addPartyId);
    const isFirst = parties.length === 0;

    const entry: CreatePartyEntry = {
      key: `${addPartyType}-${addPartyId}-${Date.now()}`,
      partyType: addPartyType,
      partyId: addPartyId,
      partyName: name,
      partyRole: addPartyRole,
      isPrimary: isFirst, // AC003: first party auto-primary
    };

    onChange([...parties, entry]);
    setShowAddRow(false);
    setAddPartyId("");
    setAddPartyRole("borrower");
  }, [addPartyId, addPartyType, addPartyRole, parties, onChange, resolvePartyName]);

  const handleRemove = useCallback(
    (key: string) => {
      const next = parties.filter((p) => p.key !== key);
      // If we removed the primary, promote the first remaining party
      if (next.length > 0 && !next.some((p) => p.isPrimary)) {
        next[0].isPrimary = true;
      }
      onChange(next);
    },
    [parties, onChange],
  );

  const handleCancelAdd = useCallback(() => {
    setShowAddRow(false);
    setAddPartyId("");
    setAddPartyRole("borrower");
  }, []);

  return (
    <div data-testid="create-parties-section" className="space-y-3 border rounded-lg p-4 bg-muted/30">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-base font-medium">
            Parties <span className="text-destructive">*</span>
          </Label>
          <p className="text-sm text-muted-foreground mt-1">
            Add borrowers, guarantors, and other parties. At least one borrower is required.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowAddRow(true)}
          disabled={showAddRow}
        >
          <Plus className="mr-2 h-3 w-3" />
          Add Party
        </Button>
      </div>

      {/* Error message */}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {/* Inline add row */}
      {showAddRow && (
        <div data-testid="create-party-add-row" className="border rounded-md p-3 bg-background space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">New Party</Label>
            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={handleCancelAdd}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {/* Type selector */}
            <div>
              <Label className="text-xs">Type</Label>
              <RadioGroup
                value={addPartyType}
                onValueChange={(v) => {
                  setAddPartyType(v as "account" | "contact");
                  setAddPartyId("");
                }}
                className="flex gap-3 mt-1"
              >
                <div className="flex items-center gap-1">
                  <RadioGroupItem value="account" id="create-party-type-account" />
                  <Label htmlFor="create-party-type-account" className="text-xs cursor-pointer">Account</Label>
                </div>
                <div className="flex items-center gap-1">
                  <RadioGroupItem value="contact" id="create-party-type-contact" />
                  <Label htmlFor="create-party-type-contact" className="text-xs cursor-pointer">Contact</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Name selector */}
            <div>
              <Label className="text-xs">Name</Label>
              <Select value={addPartyId || "__none__"} onValueChange={(v) => setAddPartyId(v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue placeholder={`Select ${addPartyType}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select {addPartyType}...</SelectItem>
                  {addPartyType === "account"
                    ? accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.legalName}</SelectItem>
                      ))
                    : contacts.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</SelectItem>
                      ))}
                </SelectContent>
              </Select>
            </div>

            {/* Role selector */}
            <div>
              <Label className="text-xs">Role</Label>
              <Select
                value={addPartyRole}
                onValueChange={setAddPartyRole}
              >
                <SelectTrigger className="h-9 mt-1" data-testid="create-party-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PARTY_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button type="button" size="sm" onClick={handleAdd} disabled={!addPartyId}>
            Add to Facility
          </Button>
        </div>
      )}

      {/* Parties table */}
      {parties.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No parties added yet. Click "Add Party" to add borrowers and guarantors.
        </p>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="h-8 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-0.5 w-[90px]">Type</TableHead>
                <TableHead className="text-xs font-medium px-2 py-0.5">Name</TableHead>
                <TableHead className="text-xs font-medium px-2 py-0.5 w-[130px]">Role</TableHead>
                <TableHead className="text-xs font-medium px-2 py-0.5 w-[80px]">Primary</TableHead>
                <TableHead className="text-xs font-medium px-2 py-0.5 w-[50px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parties.map((party, index) => (
                <TableRow key={party.key} data-testid={`create-party-row-${index}`} className="h-10">
                  <TableCell className="px-2 py-0.5 text-xs capitalize">
                    {party.partyType}
                  </TableCell>
                  <TableCell className="px-2 py-0.5 text-xs">
                    {party.partyName}
                  </TableCell>
                  <TableCell className="px-2 py-0.5 text-xs capitalize">
                    {party.partyRole.replace(/_/g, " ")}
                  </TableCell>
                  <TableCell className="px-2 py-0.5">
                    {party.isPrimary && (
                      <Badge variant="secondary" className="text-xs" data-testid="party-primary-badge">
                        Primary
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="px-2 py-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleRemove(party.key)}
                      data-testid={`create-party-row-${index}-remove`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default CreatePartiesSection;
